import { Card, CardHeader } from "@fanduel/og-fe-library";
import { ElementType, useMemo } from "react";
import PortableText from "@/components/PortableText";
import Bolt from "@/components/svg/Bolt";
import TeamPlayerAvatar from "@/components/teamPlayerAvatar";
import {
  PLAYER_NEWS_AUTHOR_LINK_CLICKED,
  PLAYER_NEWS_SOURCE_LINK_CLICKED,
  PLAYER_NEWS_TEAM_LINK_CLICKED,
  SPORTSBOOK_URL,
} from "@/constants/amplitude";
import { FALLBACK_IMAGE } from "@/constants/media";
import { ShortForm, ContentSportsEnum } from "@/graphql/gql/graphql";
import { getFormattedFullDate } from "@/utils/date";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import useMedia from "@/utils/hooks/useMedia";
import PlayerNewsMaybeLink from "../PlayerNewsMaybeLink";
import styles from "./playerNewsCard.module.scss";

interface PlayerNewsCardProps {
  news: ShortForm;
  TitleTag?: ElementType;
  origin: "article_card" | "article_page";
}

const PlayerNewsCard = ({
  news,
  TitleTag = "h2",
  origin,
}: PlayerNewsCardProps) => {
  const { logAmplitudeEvent, deviceId, sessionId } = useAmplitude();
  const player = "position" in news.primaryRef ? news.primaryRef : undefined;
  const isDesktop = useMedia("(min-width: 1024px)");

  const team = "abbreviation" in news.primaryRef ? news.primaryRef : undefined;
  const pThumbnailUrl = useMemo(() => {
    if (player) {
      return news.sport.contentSport === ContentSportsEnum.Nfl
        ? player.team?.teamJerseyImage?.url
        : player?.image?.url;
    }
    return team?.teamJerseyImage?.url ?? team?.image?.url;
  }, [player, team, news.sport.contentSport]);

  const playerSBKLink = player?.team?.sportsbookLink ?? SPORTSBOOK_URL;
  const teamSBKLink = team?.sportsbookLink ?? SPORTSBOOK_URL;

  return (
    <Card variant="elevation" elevation="low">
      <CardHeader className={styles.cardHeader}>
        <div className={styles.headerContainer}>
          <div className={styles.imageContainer}>
            <TeamPlayerAvatar
              primaryImgName={news.primaryRef.name}
              primaryImgUrl={pThumbnailUrl ?? ""}
              primaryImgSize={isDesktop ? 60 : 52}
              secondaryImgName={player?.team?.name ?? ""}
              secondaryImgUrl={player?.team?.image?.url ?? FALLBACK_IMAGE}
              secondaryImgSize={isDesktop ? 30 : 26}
            />
          </div>
          <div className={styles.playerInfo}>
            <h2 className={styles.playerName}>{news.primaryRef.name}</h2>
            {player ? (
              <p>
                <a
                  onClick={() =>
                    logAmplitudeEvent(PLAYER_NEWS_TEAM_LINK_CLICKED, {
                      "Link Text": player.team?.name ?? "",
                      "Link URL": playerSBKLink ?? "",
                      ampDeviceId: deviceId ?? "",
                      ampSessionId: sessionId ?? "",
                      module: "player_news_article",
                      Competition: news.sport.name,
                    })
                  }
                  href={playerSBKLink ?? ""}
                >
                  {player.team?.name}
                </a>{" "}
                {player.number ? `• #${player.number} ` : ""}• {player.position}{" "}
              </p>
            ) : (
              ""
            )}
            {team ? (
              <p>
                <a
                  onClick={() =>
                    logAmplitudeEvent(PLAYER_NEWS_TEAM_LINK_CLICKED, {
                      "Link Text": team?.name ?? "",
                      "Link URL": teamSBKLink,
                      module: "player_news_article",
                      Competition: news.sport.name,
                      ampDeviceId: deviceId ?? "",
                      ampSessionId: sessionId ?? "",
                    })
                  }
                  href={teamSBKLink}
                >
                  {team.name}
                </a>
              </p>
            ) : (
              ""
            )}
          </div>
        </div>
      </CardHeader>
      <div className={styles.cardContent}>
        <TitleTag className={styles.newsTitle}>
          <PlayerNewsMaybeLink news={news} />
        </TitleTag>
        <PortableText value={JSON.parse(news.fact)} />
        <div className={styles.wimContainer}>
          <h3>
            <Bolt />
            What It Means
          </h3>
          <PortableText value={JSON.parse(news.analysis)} />
        </div>
      </div>
      <div className={styles.cardFooter}>
        {news.attribution ? (
          <div>
            Source:{" "}
            <a
              target="_blank"
              onClick={() =>
                logAmplitudeEvent(PLAYER_NEWS_SOURCE_LINK_CLICKED, {
                  "Link Text": news.attribution?.name ?? "",
                  "Link URL": news.attribution?.link ?? "",
                  module: origin,
                  Competition: news.sport.name,
                })
              }
              href={news.attribution.link ?? ""}
              rel="noreferrer"
            >
              {news.attribution.name}
            </a>
          </div>
        ) : (
          ""
        )}
        <div>
          Written by{" "}
          <a
            onClick={() =>
              logAmplitudeEvent(PLAYER_NEWS_AUTHOR_LINK_CLICKED, {
                "Link Text": news.author.name ?? "",
                "Link URL": news.author.socialMedia?.[0]?.url ?? "",
                module: origin,
                Competition: news.sport.name,
              })
            }
            target="_blank"
            href={news.author.socialMedia?.[0]?.url}
            rel="noreferrer"
          >
            {news.author.name}
          </a>{" "}
          on{" "}
          <time
            dateTime={
              news.lastPublishedAt ?? news.firstPublishedAt ?? news.modifiedDate
            }
            suppressHydrationWarning
          >
            {getFormattedFullDate(
              news.lastPublishedAt ?? news.firstPublishedAt ?? news.modifiedDate
            )}
          </time>
        </div>
      </div>
    </Card>
  );
};

export default PlayerNewsCard;
